import { useState, useEffect, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { LineChart, ArrowLeft, Loader2, Activity, CalendarCheck, Users, Gauge, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const iso = (d) => d.toISOString().slice(0, 10);
const QUICK = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

export default function AdminMemberInsights() {
  const { user } = useAuth();
  const today = useMemo(() => new Date(), []);
  const [start, setStart] = useState(iso(new Date(Date.now() - 29 * 864e5)));
  const [end, setEnd] = useState(iso(today));
  const [memberId, setMemberId] = useState("");
  const [members, setMembers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setMembers(data || [])).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams({ start, end });
    if (memberId) qs.set("user_id", memberId);
    api.get(`/admin/wellness-analytics?${qs.toString()}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, memberId]);

  const applyQuick = (days) => {
    setEnd(iso(today));
    setStart(iso(new Date(Date.now() - (days - 1) * 864e5)));
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ start, end });
      if (memberId) qs.set("user_id", memberId);
      const res = await api.get(`/admin/wellness-export?${qs.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sisterhood-wellness-${start}-to-${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't export right now. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (user && user.role !== "owner") return <Navigate to="/home" replace />;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="admin-member-insights-page">
      <Link to="/settings" className="inline-flex items-center gap-2 text-sm text-[#59535B] hover:text-[#291B2D] transition-smooth" data-testid="admin-insights-back">
        <ArrowLeft className="h-4 w-4" /> Back to settings
      </Link>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <LineChart className="text-[#C89B55]" size={22} />
          <div>
            <h1 className="text-2xl font-serif text-[#291B2D]">Member Insights</h1>
            <p className="text-sm text-[#59535B]">Wellness score progress and daily check-in rates over time.</p>
          </div>
        </div>
        <button onClick={exportCsv} disabled={exporting || loading} data-testid="insights-export-csv"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] transition-smooth disabled:opacity-60">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <section className="bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-5 space-y-4" data-testid="insights-filters">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1 text-sm text-[#59535B]">
            From
            <input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
              data-testid="insights-start-date"
              className="px-3 py-2 rounded-lg border border-[#E5DED4] bg-white text-[#291B2D] focus:outline-none focus:border-[#C89B55]" />
          </label>
          <label className="flex flex-col gap-1 text-sm text-[#59535B]">
            To
            <input type="date" value={end} min={start} max={iso(today)} onChange={(e) => setEnd(e.target.value)}
              data-testid="insights-end-date"
              className="px-3 py-2 rounded-lg border border-[#E5DED4] bg-white text-[#291B2D] focus:outline-none focus:border-[#C89B55]" />
          </label>
          <label className="flex flex-col gap-1 text-sm text-[#59535B] flex-1 min-w-[200px]">
            Member
            <select value={memberId} onChange={(e) => setMemberId(e.target.value)}
              data-testid="insights-member-select"
              className="px-3 py-2 rounded-lg border border-[#E5DED4] bg-white text-[#291B2D] focus:outline-none focus:border-[#C89B55]">
              <option value="">All members</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name || "Unnamed"} — {m.email}</option>
              ))}
            </select>
          </label>
          <div className="flex gap-1 bg-[#F0E9DE] rounded-full p-1" data-testid="insights-quick-ranges">
            {QUICK.map((r) => (
              <button key={r.days} onClick={() => applyQuick(r.days)} data-testid={`insights-quick-${r.days}`}
                className="px-4 py-1.5 rounded-full text-sm text-[#59535B] hover:text-[#291B2D] transition-smooth">
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#8A838E]" data-testid="insights-loading">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : !data ? (
        <p className="text-[#8A838E]">Couldn't load insights right now.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard testid="insights-avg-score" icon={<Gauge className="h-4 w-4" />} label="Avg wellness score"
              value={data.avg_wellness_score == null ? "—" : `${data.avg_wellness_score}`} suffix={data.avg_wellness_score == null ? "" : "/100"} />
            <StatCard testid="insights-checkin-rate" icon={<CalendarCheck className="h-4 w-4" />}
              label={memberId ? "Days checked in" : "Check-in rate"} value={`${data.checkin_rate_pct}%`} />
            <StatCard testid="insights-total-checkins" icon={<Activity className="h-4 w-4" />} label="Total check-ins" value={data.total_checkins.toLocaleString()} />
            <StatCard testid="insights-active-members" icon={<Users className="h-4 w-4" />}
              label={memberId ? "Member" : "Active members"} value={memberId ? "1" : `${data.active_members}/${data.total_members}`} />
          </div>

          <section data-testid="insights-score-chart">
            <h2 className="text-sm font-semibold text-[#291B2D] mb-3">Wellness score over time</h2>
            <div className="bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-5">
              <ScoreChart series={data.series} />
            </div>
          </section>

          <section data-testid="insights-checkin-chart">
            <h2 className="text-sm font-semibold text-[#291B2D] mb-3">
              {memberId ? "Daily check-ins" : "Members checking in per day"}
            </h2>
            <div className="bg-[#FCFAF6] rounded-2xl border border-[#E5DED4] p-5">
              <CheckinBars series={data.series} perMember={!!memberId} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, suffix, testid }) {
  return (
    <div className="bg-[#291B2D] rounded-2xl p-5 text-[#FCFAF6]" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#C89B55] text-xs uppercase tracking-wide">{icon}{label}</div>
      <div className="text-3xl font-light mt-2">{value}<span className="text-base text-[#A298A6]">{suffix}</span></div>
    </div>
  );
}

function ScoreChart({ series }) {
  const W = 720, H = 220, PAD = 36;
  const pts = series.map((s, i) => ({ ...s, i })).filter((s) => s.avg_score != null);
  if (pts.length === 0) {
    return <p className="text-sm text-[#8A838E]" data-testid="insights-score-empty">No check-ins in this range yet.</p>;
  }
  const n = series.length;
  const x = (i) => PAD + (n <= 1 ? 0 : (i / (n - 1)) * (W - 2 * PAD));
  const y = (v) => PAD + (1 - v / 100) * (H - 2 * PAD);
  const line = pts.map((p, k) => `${k === 0 ? "M" : "L"}${x(p.i).toFixed(1)},${y(p.avg_score).toFixed(1)}`).join(" ");
  const gridY = [0, 25, 50, 75, 100];
  const labelEvery = Math.max(1, Math.ceil(n / 6));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="insights-score-svg" style={{ maxHeight: 260 }}>
      {gridY.map((g) => (
        <g key={g}>
          <line x1={PAD} x2={W - PAD} y1={y(g)} y2={y(g)} stroke="#EFE7DB" strokeWidth="1" />
          <text x={PAD - 8} y={y(g) + 4} textAnchor="end" fontSize="11" fill="#8A838E">{g}</text>
        </g>
      ))}
      <path d={line} fill="none" stroke="#C89B55" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p) => (
        <circle key={p.i} cx={x(p.i)} cy={y(p.avg_score)} r="3.5" fill="#291B2D">
          <title>{p.date}: {p.avg_score}/100 · {p.checkin_count} check-in(s)</title>
        </circle>
      ))}
      {series.map((s, i) => (i % labelEvery === 0 || i === n - 1) ? (
        <text key={s.date} x={x(i)} y={H - 10} textAnchor="middle" fontSize="10" fill="#8A838E">
          {s.date.slice(5)}
        </text>
      ) : null)}
    </svg>
  );
}

function CheckinBars({ series, perMember }) {
  const max = Math.max(1, ...series.map((s) => (perMember ? s.checkin_count : s.members_checked_in)));
  const any = series.some((s) => (perMember ? s.checkin_count : s.members_checked_in) > 0);
  if (!any) {
    return <p className="text-sm text-[#8A838E]" data-testid="insights-checkin-empty">No check-ins in this range yet.</p>;
  }
  return (
    <div className="flex items-end gap-[2px] h-36" data-testid="insights-checkin-bars">
      {series.map((s) => {
        const v = perMember ? s.checkin_count : s.members_checked_in;
        return (
          <div key={s.date} className="flex-1 flex flex-col items-center justify-end h-full"
            title={`${s.date}: ${v}${perMember ? " check-in(s)" : " member(s)"}`}>
            <div className="w-full rounded-t-sm transition-all" style={{ height: `${(v / max) * 100}%`, background: v ? "#C89B55" : "#EFE7DB", minHeight: v ? 3 : 1 }} />
          </div>
        );
      })}
    </div>
  );
}
